import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { memo, useEffect, useMemo, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet, View, type ViewStyle } from 'react-native';

/**
 * Lightweight one-shot confetti burst — react-native `Animated` based.
 *
 * Intended as a small celebratory flourish over a terminal UI state (e.g. the
 * expert just submitted references for ENG-492). Particles fire outward from
 * roughly the center of the viewport, rise briefly, then fall under "gravity"
 * while rotating and fading. Keep the burst short and scoped so it feels warm,
 * not patronizing.
 *
 * - Uses react-native `Animated` (not Reanimated): Reanimated's transforms
 *   have been flaky on RN-Web; basic `Animated` compiles to CSS transforms and
 *   works identically on web.
 * - On web the container uses `position: fixed` and a high `z-index` so the
 *   burst sits above modal/action-sheet stacking contexts that would otherwise
 *   cover it. On native it uses `position: absolute` inside the parent.
 * - Renders `pointerEvents="none"` so it never blocks touch.
 * - Calls `onComplete` after the last particle fades, letting the parent
 *   unmount the burst.
 * - A ~1.4s one-shot decorative flourish is within WCAG 2.3.3 guidance;
 *   reduced-motion users still get static feedback via the resolved bubble.
 */
type ConfettiBurstProps = {
  /** Called once all particles have faded. Parent typically unmounts the burst. */
  onComplete?: () => void;
  /** Override the particle count. Defaults to 28. */
  count?: number;
};

const DEFAULT_PARTICLE_COUNT = 28;

const BURST_DURATION_MS = 1400;

const RISE_FRACTION = 380 / BURST_DURATION_MS;

/** Design-system semantic colors — keeps the burst on-brand across themes. */
const PARTICLE_COLORS = [
  '#22c55e', // primary-500 (green)
  '#3b82f6', // secondary-500 (blue)
  '#f97316', // tertiary-500 (orange)
  '#eab308', // warning-500 (amber)
  '#06b6d4', // info-500 (cyan)
  '#ec4899', // accent pink
];

type ParticleSpec = {
  color: string;
  size: number;
  /** Horizontal drift at end of animation, in px. */
  driftX: number;
  /** Peak rise height above the origin, in px. */
  peakY: number;
  /** Total fall distance below the origin, in px. */
  fallY: number;
  /** Total rotation, in degrees. Signed. */
  rotation: number;
  /** Delay before this particle starts animating, in ms. */
  delay: number;
  /** Render as a circle vs. a square. */
  isCircle: boolean;
};

function buildParticleSpecs(count: number): ParticleSpec[] {
  return Array.from({ length: count }, (_, index) => {
    const angle = (Math.PI * (index + Math.random())) / count;

    const power = 80 + Math.random() * 140;

    return {
      color: PARTICLE_COLORS[index % PARTICLE_COLORS.length],
      size: 8 + Math.random() * 6,
      driftX: Math.cos(angle) * power * (Math.random() < 0.5 ? -1 : 1),
      peakY: -(60 + Math.random() * 80),
      fallY: 220 + Math.random() * 180,
      rotation: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 540),
      delay: Math.floor(Math.random() * 180),
      isCircle: index % 2 === 0,
    };
  });
}

function Particle({ spec }: { spec: ParticleSpec }) {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    progress.setValue(0);

    Animated.sequence([
      Animated.delay(spec.delay),
      Animated.timing(progress, {
        toValue: 1,
        duration: BURST_DURATION_MS,
        // JS driver: native driver doesn't support interpolated rotation strings
        // on all platforms, and the JS driver works identically on RN-Web.
        useNativeDriver: false,
        easing: Easing.out(Easing.quad),
      }),
    ]).start();
  }, [progress, spec]);

  const riseProgress = progress.interpolate({
    inputRange: [0, RISE_FRACTION, 1],
    outputRange: [0, 1, 0],
  });

  const fallProgress = progress.interpolate({
    inputRange: [0, RISE_FRACTION, 1],
    outputRange: [0, 0, 1],
  });

  const translateX = Animated.multiply(progress, spec.driftX);

  const translateY = Animated.add(
    Animated.multiply(riseProgress, spec.peakY),
    Animated.multiply(fallProgress, spec.fallY)
  );

  const rotate = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', `${spec.rotation}deg`],
  });

  const opacity = progress.interpolate({
    inputRange: [0, 0.1, 0.65, 1],
    outputRange: [0, 1, 1, 0],
  });

  return (
    <Animated.View
      style={[
        styles.particle,
        {
          width: spec.size,
          height: spec.size,
          backgroundColor: spec.color,
          borderRadius: spec.isCircle ? spec.size / 2 : 2,
          opacity,
          transform: [{ translateX }, { translateY }, { rotate }],
        },
      ]}
    />
  );
}

/** Web uses `position: fixed` via a raw style prop cast; RN types don't
 *  include 'fixed' but RN-Web accepts it. */
const WEB_FIXED_CONTAINER_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999,
  pointerEvents: 'none',
} as unknown as ViewStyle;

export const ConfettiBurst = memo(function ConfettiBurst({
  onComplete,
  count = DEFAULT_PARTICLE_COUNT,
}: ConfettiBurstProps) {
  const specs = useMemo(() => buildParticleSpecs(count), [count]);

  useEffect(() => {
    if (!onComplete) {
      return;
    }

    const id = setTimeout(() => onComplete(), BURST_DURATION_MS + 250);

    return () => clearTimeout(id);
  }, [onComplete]);

  return (
    <View
      style={[isWeb ? WEB_FIXED_CONTAINER_STYLE : styles.container, { pointerEvents: 'none' }]}
      {...(Platform.OS === 'web'
        ? { 'aria-hidden': true }
        : { accessibilityElementsHidden: true })}>
      {specs.map((spec, index) => (
        <Particle key={`particle-${index}`} spec={spec} />
      ))}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    elevation: 99999,
  },
  particle: {
    position: 'absolute',
  },
});
